import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { useAuth } from "../context/AuthContext";
import { Lock, Mail } from "lucide-react";

const LOGIN_IMAGE = "https://customer-assets-jt897jd0.emergentagent.net/job_tablero-cruz-roja/artifacts/2m74qqsr_629377737_18400293682181271_4292141676485281487_n.jpg";

export default function LoginPage() {
  const { login } = useAuth();
  const nav = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true); setErr("");
    const res = await login(email, password);
    setLoading(false);
    if (res.ok) { toast.success("Bienvenido(a) al Panel CRE-JPP"); nav("/"); }
    else { setErr(res.error); toast.error(res.error); }
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2 bg-white">
      {/* Left panel — photo of institutional staff */}
      <div className="hidden lg:block relative overflow-hidden">
        <img
          src={LOGIN_IMAGE}
          alt="Equipo Cruz Roja Ecuatoriana - Junta Provincial de Pichincha"
          className="absolute inset-0 w-full h-full object-cover"
        />
        {/* Dark overlay for text readability */}
        <div className="absolute inset-0 bg-gradient-to-t from-slate-900/90 via-slate-900/40 to-slate-900/60" />
        {/* Red accent bar */}
        <div className="absolute top-0 left-0 h-full w-1 bg-crejpp" />

        <div className="relative z-10 h-full flex flex-col justify-between p-12 text-white">
          <div className="flex items-center gap-3">
            <div className="brand-logo" />
            <div>
              <div className="font-heading font-bold text-lg">CRE-JPP</div>
              <div className="text-[11px] uppercase tracking-widest text-slate-300">Panel de Tareas KPI</div>
            </div>
          </div>

          <div className="space-y-4 max-w-md">
            <div className="text-[11px] uppercase tracking-widest text-red-200 font-semibold">Cruz Roja Ecuatoriana</div>
            <h1 className="font-heading font-bold text-4xl leading-tight tracking-tight drop-shadow-lg">
              Junta Provincial<br />de Pichincha
            </h1>
            <p className="text-slate-100 text-sm leading-relaxed drop-shadow-md">
              Herramienta institucional para el control de trabajos pendientes,
              gestión de proyectos y monitoreo de indicadores clave por área.
            </p>
          </div>

          <div className="text-[11px] text-slate-300 uppercase tracking-widest">
            © {new Date().getFullYear()} · Uso institucional interno
          </div>
        </div>
      </div>

      {/* Right form */}
      <div className="flex items-center justify-center p-8 sm:p-12">
        <div className="w-full max-w-md">
          <div className="lg:hidden flex items-center gap-3 mb-8">
            <div className="brand-logo" />
            <div>
              <div className="font-heading font-bold text-slate-900">CRE-JPP</div>
              <div className="text-[10px] uppercase tracking-widest text-slate-500">Panel de Tareas KPI</div>
            </div>
          </div>

          <div className="text-[11px] uppercase tracking-widest text-slate-500 font-semibold mb-3">Acceso institucional</div>
          <h2 className="font-heading text-3xl font-bold text-slate-900 tracking-tight mb-2">Iniciar sesión</h2>
          <p className="text-sm text-slate-500 mb-8">
            Ingrese su correo institucional para acceder al panel.
          </p>

          <form onSubmit={submit} className="space-y-5">
            <div>
              <label className="block text-xs font-semibold uppercase tracking-wider text-slate-600 mb-2">Correo institucional</label>
              <div className="relative">
                <Mail className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                <input
                  data-testid="login-email"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="nombre@cruzrojapichincha.org"
                  required
                  className="w-full pl-10 pr-3 py-2.5 border border-slate-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-red-500/40 focus:border-crejpp"
                />
              </div>
            </div>
            <div>
              <label className="block text-xs font-semibold uppercase tracking-wider text-slate-600 mb-2">Contraseña</label>
              <div className="relative">
                <Lock className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                <input
                  data-testid="login-password"
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="••••••••"
                  required
                  className="w-full pl-10 pr-3 py-2.5 border border-slate-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-red-500/40 focus:border-crejpp"
                />
              </div>
            </div>
            {err && (
              <div data-testid="login-error" className="text-sm text-crejpp bg-red-50 border border-red-100 rounded-md px-3 py-2">
                {err}
              </div>
            )}
            <button
              data-testid="login-submit-button"
              type="submit"
              disabled={loading}
              className="w-full py-2.5 bg-crejpp hover:bg-crejpp-dark text-white font-semibold rounded-md text-sm transition-colors disabled:opacity-60 disabled:cursor-not-allowed"
            >
              {loading ? "Ingresando…" : "Iniciar sesión"}
            </button>
          </form>

          <p className="text-[11px] text-slate-400 mt-8 leading-relaxed">
            Al ingresar acepta el uso institucional del sistema. Toda actividad queda registrada en la bitácora de auditoría.
          </p>
        </div>
      </div>
    </div>
  );
}
